import { SELECT_MODEL__BEGIN } from "../actions/models/types";
import { ADD_ACTION_TENDENCY, DELETE_ACTION_TENDENCY, INIT_REGULATION, SAVE_REGULATION__BEGIN, SAVE_REGULATION__RESOLVE, SET_AROUSAL_SCORES, SET_EMOTION_AROUSAL_SCORE, SET_EMOTION_ATTENUATION, SET_INPUTS, SET_OUTPUTS } from "../actions/regulation/types";
import { SELECT_VERSION__BEGIN } from "../actions/versions/types";
import emotions from "../data/emotions";
import tendencies from "../data/tendencies";

const getInitState = () => ({
    actionTendencies: [],
    emotionArousalScores: [],
    emotionAttenuations: [],
    dirty: false,
    isFetching: false,
    errors: {},
});

const normalizeNumber = (value) => Number.isFinite(value) ? value : null;

const setEmotionItem = (items, emotion, data, hasValue) => {
    const next = items.filter(item => item.emotion !== emotion);
    const item = {
        ...(items.find(item => item.emotion === emotion) || {}),
        emotion,
        ...data,
    };

    return hasValue(item) ? [...next, item] : next;
};

const regulation = (state = getInitState(), action) => {
    switch (action.type) {
        case INIT_REGULATION:
            return {
                ...state,
                dirty: state.isFetching,
                errors: {},
                actionTendencies: action.actionTendencies
                    .filter(tendency => !!tendencies[tendency.code])
                    .map(tendency => ({
                        code: tendency.code,
                        inputs: tendency.emotion_inputs
                            .filter(input => !!emotions[input.emotion]),
                        outputs: tendency.emotion_outputs
                            .filter(output => !!emotions[output.emotion]),
                    })),
                arousalLowScore: action.arousalLowScore,
                arousalMediumScore: action.arousalMediumScore,
                arousalHighScore: action.arousalHighScore,
                emotionArousalScores: action.emotionArousalScores
                    ?.filter(score => !!emotions[score.emotion])
                    ?.map(score => ({
                        emotion: score.emotion,
                        lowScore: score.low_score ?? null,
                        mediumScore: score.medium_score ?? null,
                        highScore: score.high_score ?? null,
                    })) || [],
                emotionAttenuations: action.emotionAttenuations
                    ?.filter(attenuation => !!emotions[attenuation.emotion])
                    ?.map(attenuation => ({
                        emotion: attenuation.emotion,
                        hours: attenuation.hours ?? null,
                    })) || [],
            };

        case ADD_ACTION_TENDENCY:
            return {
                ...state,
                dirty: true,
                actionTendencies: [
                    ...state.actionTendencies.filter(t => t.code !== action.code),
                    {
                        code: action.code,
                        inputs: [],
                        outputs: [],
                    },
                ],
            };
        case DELETE_ACTION_TENDENCY:
            return {
                ...state,
                dirty: true,
                actionTendencies: state.actionTendencies.filter(t => t.code !== action.code),
            };

        case SET_AROUSAL_SCORES:
            return {
                ...state,
                dirty: true,
                arousalLowScore: action.arousalLowScore,
                arousalMediumScore: action.arousalMediumScore,
                arousalHighScore: action.arousalHighScore,
            };

        case SET_EMOTION_AROUSAL_SCORE:
            return {
                ...state,
                dirty: true,
                emotionArousalScores: setEmotionItem(
                    state.emotionArousalScores,
                    action.emotion,
                    { [action.field]: normalizeNumber(action.value) },
                    item => item.lowScore != null || item.mediumScore != null || item.highScore != null,
                ),
            };

        case SET_EMOTION_ATTENUATION:
            return {
                ...state,
                dirty: true,
                emotionAttenuations: setEmotionItem(
                    state.emotionAttenuations,
                    action.emotion,
                    { hours: normalizeNumber(action.hours) },
                    item => item.hours != null,
                ),
            };

        case SET_INPUTS:
            return {
                ...state,
                dirty: true,
                actionTendencies: state.actionTendencies
                    .map(t => t.code !== action.code ? t : ({
                        ...t,
                        inputs: action.inputs,
                    })),
            };

        case SET_OUTPUTS:
            return {
                ...state,
                dirty: true,
                actionTendencies: state.actionTendencies
                    .map(t => t.code !== action.code ? t : ({
                        ...t,
                        outputs: action.outputs,
                    })),
            };

        case SAVE_REGULATION__BEGIN:
            return {
                ...state,
                isFetching: true,
                errors: {},
                dirty: false,
            };
        case SAVE_REGULATION__RESOLVE:
            return {
                ...state,
                isFetching: false,
                errors: action.errors,
                dirty: Object.keys(action.errors).length > 0 ? true : state.dirty,
            };

        // Invalidation
        case SELECT_MODEL__BEGIN:
        case SELECT_VERSION__BEGIN:
            return getInitState();

        // Default
        default:
            return state;
    }
}

export const getRegulationActionTendencies = (state) => state.regulation.actionTendencies;
export const getRegulationArousalLowScore = (state) => state.regulation.arousalLowScore;
export const getRegulationArousalMediumScore = (state) => state.regulation.arousalMediumScore;
export const getRegulationArousalHighScore = (state) => state.regulation.arousalHighScore;
export const getRegulationEmotionArousalScores = (state) => state.regulation.emotionArousalScores;
export const getRegulationEmotionAttenuations = (state) => state.regulation.emotionAttenuations;
export const isRegulationDirty = (state) => state.regulation.dirty;
export const isFetchingRegulation = (state) => state.regulation.isFetching;
export const getRegulationErrors = (state) => state.regulation.errors;

export default regulation;
